//#region IMPORT
import React, { useMemo, useCallback, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  Box,
  IconButton,
  Tooltip,
  styled,
  tooltipClasses,
} from '@mui/material';
import { Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import Commons from '../../../../Services/Common/Common';
import { getColumnDefs2 } from './HAM_HAN_02_ColumnDefs';
//#endregion

const LargeTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    fontSize: '0.9rem',
    padding: theme.spacing(1),
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#4CAF50',
  },
}));

function HAM_HAN_02DetailCellRenderer(props) {
  const gridRef = useRef(null);
  const originalDetailDataRef = useRef(null); // 원본 데이터를 저장하는 ref (변경되지 않음)
  const masterRowData = props.data;

  const getDetailRowKey = useCallback(
    (row) => {
      if (!row) return '';
      if (row.hbdId) return `HBD_${row.hbdId}`;

      const masterIdentifier =
        row.hbmId && row.hbmId !== ''
          ? row.hbmId
          : row.masterRowNo || masterRowData?.no || 'TEMP';

      return `KEY_${masterIdentifier}_${row.no}`;
    },
    [masterRowData?.no]
  );

  // props.originalRowData2가 처음 설정될 때만 originalDetailDataRef에 저장
  useEffect(() => {
    if (
      props.originalRowData2 &&
      props.originalRowData2.length > 0 &&
      !originalDetailDataRef.current
    ) {
      // 깊은 복사로 저장하여 원본 데이터가 변경되지 않도록 함
      originalDetailDataRef.current = JSON.parse(
        JSON.stringify(props.originalRowData2)
      );
    }
  }, [props.originalRowData2]);

  // 현재 Master 행의 hbmId에 해당하는 Detail 데이터만 필터링
  const currentDetailData = useMemo(() => {
    if (!masterRowData?.hbmId) return [];
    return (props.rowData2 || []).filter(
      (detail) => detail.hbmId === masterRowData.hbmId
    );
  }, [masterRowData?.hbmId, props.rowData2]);

  // 그리드 API를 부모에 등록하여 모든 상세 그리드에 접근 가능하도록 함
  const HandleGridReady = useCallback(
    (params) => {
      if (props.gridRef2) {
        props.gridRef2.current = gridRef.current;
      }
      if (params?.api && props.registerDetailGrid && masterRowData?.no) {
        props.registerDetailGrid(masterRowData.no, params.api);
      }
    },
    [masterRowData?.no, props.registerDetailGrid, props.gridRef2]
  );

  useEffect(() => {
    return () => {
      if (props.unregisterDetailGrid && masterRowData?.no) {
        props.unregisterDetailGrid(masterRowData.no);
      }
      if (props.gridRef2 && props.gridRef2.current === gridRef.current) {
        props.gridRef2.current = null;
      }
    };
  }, [masterRowData?.no, props.unregisterDetailGrid, props.gridRef2]);

  // Detail 그리드 컬럼 정의
  const columnDefs = useMemo(() => getColumnDefs2(), []);

  const defaultColDef = useMemo(
    () => ({
      flex: 1,
      editable: true,
      filter: true,
      floatingFilter: true,
    }),
    []
  );

  // 행 상태에 따른 클래스 추가를 위한 rowClassRules
  const rowClassRules = useMemo(
    () => ({
      'new-row': (params) => {
        return params.data?.newYn === 'Y';
      },
      'edit-row': (params) => {
        return (
          params.data?.editYn === 'Y' &&
          params.data?.newYn !== 'Y' &&
          params.data?.deleteYn !== 'Y'
        );
      },
      'delete-row': (params) => {
        return params.data?.deleteYn === 'Y';
      },
    }),
    []
  );

  const localeText = useMemo(
    () => ({
      // 기본 텍스트
      page: '페이지',
      more: '더보기',
      to: '~',
      of: '/',
      next: '다음',
      last: '마지막',
      first: '처음',
      previous: '이전',
      loadingOoo: '불러오는 중...',

      // 필터 & 패널 등
      selectAll: '전체 선택',
      searchOoo: '검색...',
      blanks: '공백',

      // 페이지 사이즈 선택기
      pageSizeSelectorLabel: '페이지 당 행 수',

      // 기본 페이지네이션 설명
      paginationPageSize: '페이지 당 행 수',
      paginationNumberFormatter: (params) => `${params.value}`,
    }),
    []
  );

  const getRowId = useCallback((params) => {
    return params.data?.no;
  }, []);

  // 상세 그리드 선택 변경 핸들러
  const HandleDetailSelectionChanged = useCallback(() => {
    if (!gridRef.current?.api) return;
    if (typeof props.setSelectedRowData2 !== 'function') return;

    const selectedRows = gridRef.current.api.getSelectedRows() || [];
    props.setSelectedRowData2(selectedRows);

    if (
      typeof props.syncDetailSelectionToMaster === 'function' &&
      masterRowData?.no
    ) {
      const totalRows = gridRef.current.api.getDisplayedRowCount();
      if (totalRows > 0) {
        const allSelected = selectedRows.length === totalRows;
        props.syncDetailSelectionToMaster({
          masterNo: masterRowData.no,
          allSelected,
        });
      }
    }
  }, [
    masterRowData?.no,
    props.setSelectedRowData2,
    props.syncDetailSelectionToMaster,
  ]);

  // 상세 그리드 셀 값 변경 핸들러
  const HandleDetailCellValueChanged = useCallback(
    (event) => {
      if (
        !event?.data ||
        !event?.node ||
        !masterRowData ||
        !gridRef.current?.api
      )
        return;

      const nodeData = event.node.data;
      const updatedRow = {
        ...event.data,
        editYn: 'Y',
        masterRowNo: masterRowData?.no,
        ...(nodeData?.deleteYn === 'Y' && { deleteYn: 'Y' }),
      };

      // 그리드 노드 데이터 직접 업데이트 (재렌더링 방지)
      const rowNode = gridRef.current.api.getRowNode(updatedRow.no);
      if (rowNode) {
        rowNode.setData(updatedRow);
      }

      // changedRowData2 업데이트 (저장을 위해 필요)
      if (typeof props.setChangedRowData2 === 'function') {
        props.setChangedRowData2((prev = []) => {
          const safePrev = Array.isArray(prev) ? prev : [];
          const updatedKey = getDetailRowKey(updatedRow);
          const filtered = safePrev.filter(
            (row) => getDetailRowKey(row) !== updatedKey
          );
          const existingChangedRow = safePrev.find(
            (row) => getDetailRowKey(row) === updatedKey
          );
          const finalUpdatedRow = {
            ...updatedRow,
            ...(existingChangedRow?.deleteYn === 'Y' && { deleteYn: 'Y' }),
          };
          return [...filtered, finalUpdatedRow];
        });
      }
    },
    [getDetailRowKey, masterRowData, props.setChangedRowData2]
  );

  // 현재 Master 행의 Detail 데이터를 그리드에 반영
  useEffect(() => {
    if (!gridRef.current?.api) return;
    gridRef.current.api.setRowData(currentDetailData);
  }, [currentDetailData]);

  // 상세 그리드에 새 행 추가
  const HandleAddDetailRow = useCallback(() => {
    if (!gridRef.current?.api || !masterRowData) return;

    // 현재 그리드의 모든 행 데이터 가져오기
    const currentRows = [];
    gridRef.current.api.forEachNode((node) => {
      if (node.data) {
        currentRows.push(node.data);
      }
    });

    // 새 Detail 행 생성
    const newDetailRow = {
      no: Date.now(),
      hbmId: masterRowData.hbmId || '',
      hbdId: null,
      substanceName: '',
      casNo: '',
      contentPercent: null,
      managementTarget: 'N',
      specialManagement: 'N',
      workEnvMeasurement: 'N',
      specialHealthExam: 'N',
      permissionTarget: 'N',
      newYn: 'Y',
      editYn: 'Y',
      masterRowNo: masterRowData?.no,
    };

    // 현재 그리드의 데이터에 새 행 추가
    const updatedDetailData = [...currentRows, newDetailRow];

    // 번호 추가
    const numberedDetailData = Commons.InsertNumber(updatedDetailData);

    // 번호가 추가된 최종 행 (새로 추가된 행)
    const finalDetailRow = {
      ...numberedDetailData[numberedDetailData.length - 1],
      masterRowNo: masterRowData?.no,
    };

    // changedRowData2에 새 Detail 행 추가
    if (typeof props.setChangedRowData2 === 'function') {
      props.setChangedRowData2((prev = []) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const finalKey = getDetailRowKey(finalDetailRow);
        const filtered = safePrev.filter(
          (item) => getDetailRowKey(item) !== finalKey
        );
        return [...filtered, finalDetailRow];
      });
    }

    // 그리드에 새 행 추가 (번호가 추가된 최종 데이터 사용)
    // 기존 행들의 번호도 업데이트
    numberedDetailData.forEach((row) => {
      const existingNode = gridRef.current.api.getRowNode(row.no);
      if (
        existingNode &&
        existingNode.data &&
        existingNode.data.no !== row.no
      ) {
        // 기존 행의 번호만 업데이트
        const updatedData = { ...existingNode.data, no: row.no };
        gridRef.current.api.applyTransaction({ update: [updatedData] });
      }
    });

    // 새 행 추가
    gridRef.current.api.applyTransaction({
      add: [finalDetailRow],
      addIndex: 0,
    });

    // 첫 번째 편집 가능한 셀에 포커스
    setTimeout(() => {
      if (gridRef.current?.api) {
        const firstEditableColumn = columnDefs.find(
          (col) => col.editable && !col.hide && col.field !== 'no'
        );
        if (firstEditableColumn) {
          const field = firstEditableColumn.field;
          gridRef.current.api.setFocusedCell(0, field);
          gridRef.current.api.startEditingCell({
            rowIndex: 0,
            colKey: field,
          });
        }
      }
    }, 100);
  }, [masterRowData, props.setChangedRowData2, columnDefs]);

  // 상세 그리드 새로고침 (원본 데이터로 복원)
  const HandleRefreshDetailGrid = useCallback(() => {
    if (!gridRef.current?.api) return;

    // ref에 저장된 원본 데이터에서 현재 Master의 Detail 데이터만 필터링하여 복원
    const originalData = originalDetailDataRef.current || [];
    const dataToRestore = originalData.filter(
      (detail) => detail.hbmId === masterRowData?.hbmId
    );

    gridRef.current.api.setRowData(dataToRestore);
    gridRef.current.api.deselectAll();

    // rowData2를 원본 데이터로 복원 (현재 Master의 Detail만 업데이트)
    if (typeof props.setRowData2 === 'function') {
      props.setRowData2((prev = []) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        // 현재 Master의 Detail 제거
        const filtered = safePrev.filter(
          (detail) => detail.hbmId !== masterRowData?.hbmId
        );
        // 원본 데이터에서 현재 Master의 Detail만 추가
        return [...filtered, ...dataToRestore];
      });
    }

    // 현재 Master의 Detail 관련 상태 초기화
    if (typeof props.setSelectedRowData2 === 'function') {
      props.setSelectedRowData2((prev = []) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.filter(
          (detail) => detail.hbmId !== masterRowData?.hbmId
        );
      });
    }

    if (typeof props.setChangedRowData2 === 'function') {
      props.setChangedRowData2((prev = []) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.filter(
          (detail) => detail.hbmId !== masterRowData?.hbmId
        );
      });
    }

    if (typeof props.setDeletedRowData2 === 'function') {
      props.setDeletedRowData2((prev = []) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        return safePrev.filter(
          (detail) => detail.hbmId !== masterRowData?.hbmId
        );
      });
    }

    // 시각적 피드백
    gridRef.current.api.flashCells({
      flashDelay: 200,
      fadeDelay: 200,
    });
  }, [
    masterRowData?.hbmId,
    props.setRowData2,
    props.setSelectedRowData2,
    props.setChangedRowData2,
    props.setDeletedRowData2,
  ]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        px: 3,
        pb: 3,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          p: 0.5,
          mb: 0.5,
          overflow: 'visible',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LargeTooltip title="추가" arrow placement="top">
            <IconButton
              color="primary"
              size="small"
              sx={{ m: 0.5, border: 1 }}
              onClick={HandleAddDetailRow}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </LargeTooltip>
          <LargeTooltip title="새로고침" arrow placement="top">
            <IconButton
              color="secondary"
              size="small"
              sx={{ m: 0.5, border: 1 }}
              onClick={HandleRefreshDetailGrid}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </LargeTooltip>
        </Box>
      </Box>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={currentDetailData}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={true}
          stopEditingWhenCellsLoseFocus={true}
          pagination={true}
          paginationPageSize={100}
          paginationPageSizeSelector={[100, 200, 500, 1000]}
          localeText={localeText}
          rowClassRules={rowClassRules}
          onGridReady={HandleGridReady}
          onSelectionChanged={HandleDetailSelectionChanged}
          onCellValueChanged={HandleDetailCellValueChanged}
          style={{ height: '100%', width: '100%' }}
          className="ag-theme-alpine"
        />
      </Box>
    </Box>
  );
}

export default HAM_HAN_02DetailCellRenderer;
